'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';

interface SignUpValues {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpValues>();

  const passwordValue = watch('password');

  const onSubmit = async (data: SignUpValues) => {
    setIsLoading(true);
    // Backend integration point — POST /api/auth/register
    await new Promise((r) => setTimeout(r, 1400));
    toast.success('Account created!', {
      description: 'Upload your first syllabus to get started.',
    });
    await new Promise((r) => setTimeout(r, 600));
    router.push('/syllabus-upload-screen');
  };

  const passwordStrength = (pw: string) => {
    if (!pw) return { score: 0, label: '', color: 'transparent' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const map = [
      { score: 1, label: 'Weak', color: '#DC2626' },
      { score: 2, label: 'Fair', color: '#D97706' },
      { score: 3, label: 'Good', color: '#059669' },
      { score: 4, label: 'Strong', color: '#047857' },
    ];
    return map[score - 1] || { score: 0, label: '', color: 'transparent' };
  };

  const strength = passwordStrength(passwordValue || '');

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>
          Start for free
        </h2>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Upload your first syllabus in under 2 minutes
        </p>
      </div>

      {/* Google OAuth */}
      <button
        type="button"
        className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl border text-sm font-semibold transition-all duration-150 hover:bg-muted/40 active:scale-95"
        style={{ borderColor: 'var(--border)', color: 'var(--foreground)', backgroundColor: 'var(--card)' }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
          <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z" fill="#EA4335" />
        </svg>
        Sign up with Google
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
        <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>or</span>
        <div className="flex-1 h-px" style={{ backgroundColor: 'var(--border)' }} />
      </div>

      {/* Full Name */}
      <div className="space-y-1.5">
        <label htmlFor="signup-name" className="block text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
          Full name
        </label>
        <input
          id="signup-name"
          type="text"
          autoComplete="name"
          placeholder="Arjun Sharma"
          className="w-full px-4 py-3 rounded-xl border text-sm transition-all duration-150 outline-none focus:ring-2"
          style={{
            backgroundColor: 'var(--input)',
            borderColor: errors.fullName ? 'var(--negative)' : 'var(--border)',
            color: 'var(--foreground)',
          }}
          {...register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
        />
        {errors.fullName && <p className="text-xs font-medium" style={{ color: 'var(--negative)' }}>{errors.fullName.message}</p>}
      </div>

      {/* Email */}
      <div className="space-y-1.5">
        <label htmlFor="signup-email" className="block text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
          Email address
        </label>
        <input
          id="signup-email"
          type="email"
          autoComplete="email"
          placeholder="you@university.edu"
          className="w-full px-4 py-3 rounded-xl border text-sm transition-all duration-150 outline-none focus:ring-2"
          style={{
            backgroundColor: 'var(--input)',
            borderColor: errors.email ? 'var(--negative)' : 'var(--border)',
            color: 'var(--foreground)',
          }}
          {...register('email', {
            required: 'Email is required',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
          })}
        />
        {errors.email && <p className="text-xs font-medium" style={{ color: 'var(--negative)' }}>{errors.email.message}</p>}
      </div>

      {/* Password */}
      <div className="space-y-1.5">
        <label htmlFor="signup-password" className="block text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
          Password
        </label>
        <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
          At least 8 characters with uppercase, number, and symbol
        </p>
        <div className="relative">
          <input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Create a strong password"
            className="w-full px-4 py-3 pr-12 rounded-xl border text-sm transition-all duration-150 outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--input)',
              borderColor: errors.password ? 'var(--negative)' : 'var(--border)',
              color: 'var(--foreground)',
            }}
            {...register('password', {
              required: 'Password is required',
              minLength: { value: 8, message: 'Password must be at least 8 characters' },
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {/* Password strength bar */}
        {passwordValue && (
          <div className="space-y-1">
            <div className="flex gap-1">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={`strength-bar-${i}`}
                  className="flex-1 h-1 rounded-full transition-all duration-300"
                  style={{ backgroundColor: i <= strength.score ? strength.color : 'var(--muted)' }}
                />
              ))}
            </div>
            <p className="text-xs font-medium" style={{ color: strength.color }}>{strength.label}</p>
          </div>
        )}
        {errors.password && <p className="text-xs font-medium" style={{ color: 'var(--negative)' }}>{errors.password.message}</p>}
      </div>

      {/* Confirm Password */}
      <div className="space-y-1.5">
        <label htmlFor="signup-confirm" className="block text-sm font-semibold" style={{ color: 'var(--foreground)' }}>
          Confirm password
        </label>
        <div className="relative">
          <input
            id="signup-confirm"
            type={showConfirm ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="Repeat your password"
            className="w-full px-4 py-3 pr-12 rounded-xl border text-sm transition-all duration-150 outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--input)',
              borderColor: errors.confirmPassword ? 'var(--negative)' : 'var(--border)',
              color: 'var(--foreground)',
            }}
            {...register('confirmPassword', {
              required: 'Please confirm your password',
              validate: (val) => val === passwordValue || 'Passwords do not match',
            })}
          />
          <button
            type="button"
            onClick={() => setShowConfirm(!showConfirm)}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors"
            style={{ color: 'var(--muted-foreground)' }}
            aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
          >
            <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
          </button>
        </div>
        {errors.confirmPassword && <p className="text-xs font-medium" style={{ color: 'var(--negative)' }}>{errors.confirmPassword.message}</p>}
      </div>

      {/* Terms */}
      <div className="flex items-start gap-2.5">
        <input
          id="agree-terms"
          type="checkbox"
          className="w-4 h-4 mt-0.5 rounded accent-primary shrink-0"
          {...register('agreeTerms', { required: 'You must accept the terms to continue' })}
        />
        <label htmlFor="agree-terms" className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
          I agree to ExamReady&apos;s{' '}
          <button type="button" className="font-semibold hover:underline" style={{ color: 'var(--primary)' }}>Terms of Service</button>
          {' '}and{' '}
          <button type="button" className="font-semibold hover:underline" style={{ color: 'var(--primary)' }}>Privacy Policy</button>
        </label>
      </div>
      {errors.agreeTerms && <p className="text-xs font-medium -mt-3" style={{ color: 'var(--negative)' }}>{errors.agreeTerms.message}</p>}

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3.5 rounded-xl text-sm font-bold transition-all duration-150 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        style={{ backgroundColor: 'var(--primary)', color: 'var(--primary-foreground)' }}
      >
        {isLoading ? (
          <>
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Creating your account...
          </>
        ) : (
          'Create Free Account'
        )}
      </button>

      <p className="text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="font-semibold hover:underline" style={{ color: 'var(--primary)' }}>
          Sign in
        </button>
      </p>
    </form>
  );
}